import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Copy, Link2, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';

interface ContratoLinkItem {
  _id: string;
  token: string;
  status: string;
  expiresAt: string;
  clientLabel: string;
  clientEmail: string;
  lang: 'pt' | 'en' | 'es';
  formUrl: string;
  submittedAt?: string;
  contratoSubmission?: { nome?: string; sobrenome?: string; email?: string };
}

interface ContratoTemplateInfo {
  lang: 'en' | 'es';
  status: 'not_generated' | 'draft' | 'active';
}

const LINK_STATUS: Record<string, string> = {
  awaiting_client: 'Aguardando cliente',
  submitted: 'Enviado',
  expired: 'Expirado',
  revoked: 'Revogado'
};

const LANG_LABEL: Record<string, string> = { pt: 'Português', en: 'Inglês', es: 'Espanhol' };

const AdminContratoLinksPanel: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [items, setItems] = useState<ContratoLinkItem[]>([]);
  const [templates, setTemplates] = useState<ContratoTemplateInfo[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastUrl, setLastUrl] = useState('');
  const [linkForm, setLinkForm] = useState({
    clientLabel: '',
    clientEmail: '',
    expiresInDays: 30,
    adminNotes: '',
    lang: 'pt' as 'pt' | 'en' | 'es'
  });

  const fetchLinks = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await axios.get('/contrato/links', { params: { page: 1, limit: 10 } });
      setItems(res.data.items ?? []);
    } catch {
      notifications.error.network();
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get<ContratoTemplateInfo[]>('/contrato-templates');
      setTemplates(res.data ?? []);
    } catch {
      // Seletor de idioma cai de volta para "apenas português" se isso falhar
    }
  }, []);

  useEffect(() => {
    fetchLinks();
    fetchTemplates();
  }, [fetchLinks, fetchTemplates]);

  const createLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post('/contrato/links', linkForm);
      setLastUrl(res.data.formUrl);
      notifications.showSuccess(t('admin.contratoLinkCreated'), t('admin.contratoLinkCreatedDesc'));
      setShowForm(false);
      setLinkForm({ clientLabel: '', clientEmail: '', expiresInDays: 30, adminNotes: '', lang: 'pt' });
      fetchLinks();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('admin.contratoLinkError');
      notifications.showError(t('common.error'), msg);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    if (!(await confirm({ message: t('admin.contratoLinkRevokeConfirm'), confirmText: t('common.revoke'), variant: 'danger' }))) return;
    try {
      await axios.post(`/contrato/links/${id}/revoke`);
      fetchLinks();
    } catch {
      notifications.showError(t('common.error'), t('admin.contratoLinkRevokeError'));
    }
  };

  const remove = async (id: string) => {
    if (!(await confirm({ message: t('admin.contratoLinkDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/contrato/links/${id}`);
      fetchLinks();
    } catch {
      notifications.showError(t('common.error'), t('admin.contratoLinkDeleteError'));
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    notifications.showSuccess(t('admin.contratoLinkCopied'), '');
  };

  return (
    <div className="mb-8 bg-white rounded-xl shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('admin.contratoLinksTitle')}</h2>
          <p className="text-sm text-gray-600 mt-0.5">{t('admin.contratoLinksDesc')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('admin.contratoNewLink')}
        </button>
      </div>

      {lastUrl && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex flex-wrap items-center gap-2">
          <Link2 className="w-4 h-4 text-green-700 shrink-0" />
          <code className="text-xs sm:text-sm text-green-900 break-all flex-1">{lastUrl}</code>
          <button
            type="button"
            onClick={() => copyUrl(lastUrl)}
            className="p-2 text-green-800 hover:bg-green-100 rounded-lg transition-colors duration-200"
            title={t('admin.contratoLinkCopied')}
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setLastUrl('')}
            className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            title={t('common.close', { defaultValue: 'Fechar' })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={createLink} className="mb-4 border border-gray-100 rounded-lg p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-700">{t('admin.contratoLinkClientLabel')}</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={linkForm.clientLabel}
                onChange={(e) => setLinkForm((f) => ({ ...f, clientLabel: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">{t('admin.contratoLinkClientEmail')} *</span>
              <input
                type="email"
                required
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={linkForm.clientEmail}
                onChange={(e) => setLinkForm((f) => ({ ...f, clientEmail: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">{t('admin.contratoLinkLang')}</span>
              <select
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={linkForm.lang}
                onChange={(e) => setLinkForm((f) => ({ ...f, lang: e.target.value as 'pt' | 'en' | 'es' }))}
              >
                <option value="pt">{LANG_LABEL.pt}</option>
                {(['en', 'es'] as const).map((lang) => {
                  const isActive = templates.find((tpl) => tpl.lang === lang)?.status === 'active';
                  return (
                    <option key={lang} value={lang} disabled={!isActive}>
                      {LANG_LABEL[lang]}{!isActive ? ` (${t('admin.contratoLinkLangUnavailable')})` : ''}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-gray-700">{t('admin.contratoLinkExpiresDays')}</span>
              <input
                type="number"
                min={1}
                max={90}
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={linkForm.expiresInDays}
                onChange={(e) => setLinkForm((f) => ({ ...f, expiresInDays: Number(e.target.value) }))}
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>{t('common.sending', { defaultValue: 'Gerando...' })}</span>
                </>
              ) : (
                <span>{t('admin.contratoGenerateLink')}</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={fetchLinks}
          disabled={refreshing}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('admin.refreshList', { defaultValue: 'Atualizar' })}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {initialLoading ? (
        <p className="text-sm text-gray-500 py-4 text-center">{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">{t('admin.contratoLinksEmpty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="pb-2 pr-3">{t('admin.contratoLinkClientLabel')}</th>
                <th className="pb-2 pr-3">Status</th>
                <th className="pb-2 pr-3">{t('admin.contratoLinkLang')}</th>
                <th className="pb-2 pr-3">{t('admin.contratoLinkExpires')}</th>
                <th className="pb-2">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item._id} className="border-b border-gray-50">
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-900">
                      {item.clientLabel ||
                        (item.contratoSubmission
                          ? `${item.contratoSubmission.nome || ''} ${item.contratoSubmission.sobrenome || ''}`.trim()
                          : '—')}
                    </div>
                    <div className="text-xs text-gray-500">{item.clientEmail}</div>
                  </td>
                  <td className="py-2 pr-3">{LINK_STATUS[item.status] || item.status}</td>
                  <td className="py-2 pr-3">{LANG_LABEL[item.lang] || item.lang}</td>
                  <td className="py-2 pr-3 text-gray-600">
                    {new Date(item.expiresAt).toLocaleDateString()}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => copyUrl(item.formUrl)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                        title={t('admin.contratoLinkCopied')}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {item.status === 'awaiting_client' && (
                        <button
                          type="button"
                          onClick={() => revoke(item._id)}
                          className="p-2 text-amber-700 hover:bg-amber-100 rounded-lg transition-colors duration-200 text-xs"
                        >
                          {t('admin.contratoLinkRevoke')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(item._id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminContratoLinksPanel;
